import { Document, ISectionOptions, Packer, PageOrientation } from 'docx'
import { useFileDownload } from '@/composables/useFileDownload'
import { getTemplateConfig } from './templates'
import { set } from 'lodash'
import { getStyleConfig } from './styles'
import { Resume } from '@/types/resume/v1'

/**
 * Build list of sections based on template type.
 *
 * Note: once custom ordering of section is implemented,
 * we can apply the custom order to the sections list.
 *
 * @param resume Resume
 */
const buildSections = (resume: Resume.Schema) => {
  const { structure, basics } = getTemplateConfig(resume.meta)
  const { pageConfig: { margin: pageMargin, size: pageSize }, styles } = getStyleConfig(resume.meta)

  const sections: ISectionOptions[] = []

  if ('basics' in resume) {
    sections.push(
      ...basics(resume['basics']).map(sectionConfig => {
        set(sectionConfig, 'properties.page.size', pageSize)
        set(sectionConfig, 'properties.page.margin', pageMargin)
        return sectionConfig
      })
    )
  }

  Object.entries(structure).reduce((acc, [sectionName, sectionGenerator]) => {
    if (
      resume[sectionName]?.length ||
      (sectionName === 'skills' &&
        (resume.skills?.length || resume.categorizedSkills?.length))
    ) {
      const sectionConfig = sectionGenerator(resume)
      set(sectionConfig, 'properties.page.size', pageSize)
      set(sectionConfig, 'properties.page.margin', pageMargin)
      acc.push(sectionConfig)
    }

    return acc
  }, sections)

  return {
    sections,
    styles,
  }
}

export const buildDocument = (resume: Resume.Schema, resumeInJson) => {
  const { sections, styles } = buildSections(resume)

  return new Document({
    sections,
    styles,
    title: resumeInJson.name,
    creator: 'Jobscan',
    customProperties: [
      { name: 'ResumeInJson', value: JSON.stringify(resumeInJson) },
    ],
  })
}

export const generateDocx = (
  resume: Resume.Schema,
  documentName = 'resume',
  resumeInJson = {}
) => {
  const doc = buildDocument(resume, resumeInJson)

  Packer.toBlob(doc).then(blob => {
    useFileDownload().downloadBlob(blob, `${documentName}.docx`)
  })
}
